import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Book, Heart, Gift, CreditCard, ArrowRight } from "lucide-react";
import { TeddyBear } from '@/components/icons/TeddyBear';
import { useLocation, Link } from "wouter";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";

export default function AuthPage() {
  const { user, isLoading, loginMutation, registerMutation } = useAuth();
  const [location, navigate] = useLocation();
  
  // Get the tab from URL parameters (e.g., /auth?tab=register)
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get('tab');
  const promoParam = urlParams.get('promo') || '';
  const initialTab = tabParam === 'register' || tabParam === 'gift' ? tabParam : 'login';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    promoCode: promoParam,
    giftCardCode: "",
  });
  const [giftData, setGiftData] = useState({
    recipientEmail: "",
    recipientName: "",
    subscriptionDuration: "monthly",
    message: "",
  });
  const [giftValidationErrors, setGiftValidationErrors] = useState<{ [key: string]: string }>({});
  const [giftCardNumber, setGiftCardNumber] = useState("");
  const [isCheckingCard, setIsCheckingCard] = useState(false);
  const { toast } = useToast();

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoading && user) {
      // If user has a pending gift card code, redirect to redeem page
      if (user.pendingGiftCardCode) {
        navigate(`/redeem?card=${user.pendingGiftCardCode}`);
      } else {
        navigate("/home");
      }
    }
  }, [user, isLoading, navigate]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data before submission
    if (!loginData.email || !loginData.password) {
      toast({
        title: "Missing information",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      return;
    }
    
    // Trim and validate email format
    const trimmedEmail = loginData.email.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    // Submit with trimmed email
    loginMutation.mutate({
      email: trimmedEmail,
      password: loginData.password,
    });
  };

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    registerMutation.mutate(registerData);
  };

  const handleGiftPurchase = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setGiftValidationErrors({});
    
    // Validate required fields
    const errors: { [key: string]: string } = {};
    
    if (!giftData.recipientEmail.trim()) {
      errors.recipientEmail = "Recipient's email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(giftData.recipientEmail)) {
      errors.recipientEmail = "Please enter a valid email address";
    }
    
    // If there are validation errors, show them and don't proceed
    if (Object.keys(errors).length > 0) {
      setGiftValidationErrors(errors);
      return;
    }
    
    // Redirect to gift purchase flow - user needs to be logged in first
    const giftParams = new URLSearchParams({
      recipientEmail: giftData.recipientEmail,
      recipientName: giftData.recipientName,
      subscriptionDuration: giftData.subscriptionDuration,
      message: giftData.message,
    });
    navigate(`/gift-subscription?${giftParams.toString()}`);
  };

  const handleCheckGiftCard = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!giftCardNumber.trim()) {
      toast({
        title: "Error",
        description: "Please enter a gift card number",
        variant: "destructive",
      });
      return;
    }

    setIsCheckingCard(true);

    try {
      const response: any = await apiRequest("GET", `/api/cardivo/gift-cards/status/${giftCardNumber.trim().toUpperCase()}`);
      
      toast({
        title: "Gift Card Found!",
        description: `This gift card is worth $${response.value}. Sign in or create an account to redeem it.`,
      });
      
      // Redirect to dedicated redeem page
      navigate(`/redeem?card=${giftCardNumber.trim().toUpperCase()}`);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to check gift card",
        variant: "destructive",
      });
    } finally {
      setIsCheckingCard(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex flex-col">
      <Navigation />
      {/* Main content area */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left side - Auth forms */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6 lg:mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/logo.png" 
                alt="SnuggleCrafters Logo" 
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
              />
              <div className="flex flex-col items-center">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">SnuggleCrafters.com</h1>
                <span className="text-xs sm:text-sm text-gray-600">by VibeCaaS.com</span>
              </div>
            </div>
            <p className="text-gray-700 mt-2 text-sm sm:text-base">Create magical bedtime stories with AI</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Sign Up</TabsTrigger>
              <TabsTrigger value="gift" className="flex items-center gap-1">
                <Gift className="w-4 h-4" />
                <span className="hidden sm:inline">Gift</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle>Welcome back!</CardTitle>
                  <CardDescription>
                    Sign in to continue creating magical stories
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginData.email}
                        onChange={(e) =>
                          setLoginData({ ...loginData, email: e.target.value })
                        }
                        required
                        aria-label="Email address for login"
                        aria-required="true"
                        aria-invalid={loginMutation.error ? "true" : "false"}
                        aria-describedby={loginMutation.error ? "login-error" : undefined}
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginData.password}
                        onChange={(e) =>
                          setLoginData({ ...loginData, password: e.target.value })
                        }
                        required
                        aria-label="Password for login"
                        aria-required="true"
                        aria-invalid={loginMutation.error ? "true" : "false"}
                      />
                    </div>
                    <div className="text-right">
                      <a
                        href="/forgot-password"
                        className="text-sm text-purple-600 hover:text-purple-700 hover:underline"
                      >
                        Forgot your password?
                      </a>
                    </div>
                    {loginMutation.error && (
                      <Alert variant="destructive" role="alert" id="login-error">
                        <AlertDescription>
                          {loginMutation.error.message}
                        </AlertDescription>
                      </Alert>
                    )}
                    <Button
                      type="submit"
                      className="w-full text-white"
                      variant="gradient"
                      size="lg"
                      disabled={loginMutation.isPending}
                    >
                      {loginMutation.isPending ? "Signing in..." : "Sign in"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle>Create your account</CardTitle>
                  <CardDescription>
                    Start your 7-day free trial today!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                          id="firstName"
                          value={registerData.firstName}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              firstName: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          value={registerData.lastName}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              lastName: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            email: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        value={registerData.password}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            password: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="promo-code">Promo Code (Optional)</Label>
                      <Input
                        id="promo-code"
                        type="text"
                        placeholder="Enter promo code"
                        value={registerData.promoCode}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            promoCode: e.target.value,
                          })
                        }
                        data-testid="input-promo-code-register"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Have a promo code? Enter it here for a discount on your subscription.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="gift-card-code">Gift Card Code (Optional)</Label>
                      <Input
                        id="gift-card-code"
                        type="text"
                        placeholder="Enter gift card code"
                        value={registerData.giftCardCode}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            giftCardCode: e.target.value.toUpperCase(),
                          })
                        }
                        data-testid="input-gift-card-code-register"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Have a gift card? Enter the code here to redeem it after signing up.
                      </p>
                    </div>
                    {registerMutation.error && (
                      <Alert variant="destructive">
                        <AlertDescription>
                          {registerMutation.error.message}
                        </AlertDescription>
                      </Alert>
                    )}
                    <Button
                      type="submit"
                      className="w-full text-white"
                      variant="gradient"
                      size="lg"
                      disabled={registerMutation.isPending}
                    >
                      {registerMutation.isPending ? "Creating account..." : "Start Free Trial"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="gift">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-purple-600" />
                    Gift Options
                  </CardTitle>
                  <CardDescription>
                    Give the gift of magical bedtime stories to someone special
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Gift Card Notice */}
                  <div className="bg-gradient-to-r from-purple-50 to-teal-50 dark:from-purple-900/20 dark:to-teal-900/20 p-4 rounded-lg border-2 border-purple-200 dark:border-purple-700">
                    <div className="flex items-start gap-3">
                      <Gift className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                          Buy a Gift Card - No Account Required!
                        </h3>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                          Purchase a digital gift card for any amount. Recipients can redeem it without creating an account first.
                        </p>
                        <Link href="/buy-gift-card">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-purple-300 hover:bg-purple-100 dark:border-purple-600 dark:hover:bg-purple-900/40"
                            data-testid="link-buy-gift-card-auth"
                          >
                            Buy Gift Card Now
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>

                  {/* Gift Subscription Form */}
                  <div>
                    <h3 className="font-semibold mb-2">Gift a Specific Subscription Plan</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Purchase a subscription plan as a gift (requires sign in)
                    </p>
                  </div>
                  
                  <form onSubmit={handleGiftPurchase} className="space-y-4">
                    <div>
                      <Label htmlFor="recipient-email">Recipient's Email</Label>
                      <Input
                        id="recipient-email"
                        type="email"
                        placeholder="Their email address"
                        value={giftData.recipientEmail}
                        onChange={(e) =>
                          setGiftData({ ...giftData, recipientEmail: e.target.value })
                        }
                        className={giftValidationErrors.recipientEmail ? "border-red-500" : ""}
                        data-testid="input-recipient-email"
                      />
                      {giftValidationErrors.recipientEmail && (
                        <p className="text-sm text-red-600 mt-1" data-testid="error-recipient-email">
                          {giftValidationErrors.recipientEmail}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="recipient-name">Recipient's Name</Label>
                      <Input
                        id="recipient-name"
                        type="text"
                        placeholder="Their name (optional)"
                        value={giftData.recipientName}
                        onChange={(e) =>
                          setGiftData({ ...giftData, recipientName: e.target.value })
                        }
                        data-testid="input-recipient-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subscription-months">Subscription Duration</Label>
                      <Select
                        value={giftData.subscriptionDuration}
                        onValueChange={(value) =>
                          setGiftData({ ...giftData, subscriptionDuration: value })
                        }
                      >
                        <SelectTrigger id="subscription-months" data-testid="select-subscription-duration">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly - $6.99/month</SelectItem>
                          <SelectItem value="annual">Yearly - $69.99/year (Save $13.89!)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="gift-message">Personal Message (Optional)</Label>
                      <Textarea
                        id="gift-message"
                        placeholder="Add a personal message to your gift..."
                        value={giftData.message}
                        onChange={(e) =>
                          setGiftData({ ...giftData, message: e.target.value })
                        }
                        rows={3}
                        data-testid="textarea-gift-message"
                      />
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg space-y-2">
                      <p className="text-sm font-medium text-purple-900">How it works:</p>
                      <ul className="text-sm text-purple-700 space-y-1">
                        <li>• You'll be taken to checkout after clicking below</li>
                        <li>• Recipient gets an email with their gift code</li>
                        <li>• They can redeem it anytime within 1 year</li>
                        <li>• No billing until they activate their subscription</li>
                      </ul>
                    </div>
                    <Button
                      type="submit"
                      className="w-full text-white"
                      variant="gradient"
                      size="lg"
                      data-testid="button-continue-to-checkout"
                    >
                      <Gift className="w-4 h-4 mr-2" />
                      Continue to Checkout
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      You'll need to sign in or create an account to complete the purchase
                    </p>
                  </form>

                  <Separator className="my-6" />

                  {/* Gift Card Redemption Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-semibold">Redeem a Gift Card</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Have a SnuggleCrafters gift card? Enter it below to check its value and redeem it.
                    </p>
                    
                    <form onSubmit={handleCheckGiftCard} className="space-y-4">
                      <div>
                        <Label htmlFor="gift-card-number">Gift Card Number</Label>
                        <Input
                          id="gift-card-number"
                          type="text"
                          placeholder="Enter gift card number"
                          value={giftCardNumber}
                          onChange={(e) => setGiftCardNumber(e.target.value.toUpperCase())}
                          data-testid="input-gift-card-auth"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        variant="outline"
                        disabled={isCheckingCard}
                        data-testid="button-check-gift-card-auth"
                      >
                        {isCheckingCard ? "Checking..." : "Check Gift Card"}
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right side - Hero content */}
      <div className="flex-1 bg-gradient-to-br from-primary via-secondary to-accent p-8 text-black flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-600 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <img
                src="/teddy.svg"
                alt="Teddy Bear"
                width="48"
                height="48"
                className="text-white"
              />
            </div>
            <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent">
              Magical Stories Await
            </h2>
            <p className="text-xl mb-6 text-black">
              Create personalized bedtime stories powered by AI, complete with illustrations, audio narration, and more!
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-left">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <TeddyBear className="w-5 h-5 text-black" />
              </div>
              <span className="text-black">Unlimited AI-generated stories</span>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <TeddyBear className="w-5 h-5 text-black" />
              </div>
              <span className="text-black">Beautiful illustrations & narration</span>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <TeddyBear className="w-5 h-5 text-black" />
              </div>
              <span className="text-black">Personalized for your child</span>
            </div>
          </div>

          <div className="mt-8 p-4 bg-white/10 rounded-lg">
            <p className="text-sm text-black">
              <strong>7-day free trial</strong> then only <strong>$6.99/month</strong>
            </p>
          </div>
        </div>
      </div>
      </div>
      
      <Footer />
    </div>
  );
}